export const IFS_INTRO = `Internal Family Systems (IFS), developed by Dr Richard Schwartz, starts from a radical idea: you are not one single mind. You are a system of parts — inner voices, feelings and roles that each carry a job. Underneath them all is Self: calm, curious, compassionate, and never damaged, no matter what happened to you. IFS isn't about getting rid of parts. It's about getting to know them.`;

export const IFS_SELF_QUALITIES = [
  "Calm — steady enough to stay present",
  "Curiosity — wanting to understand rather than fix",
  "Compassion — warmth toward the part, not judgement",
  "Clarity — seeing without the fog of the part's story",
  "Courage — willing to hear what's hard",
  "Confidence — trusting you can hold it",
  "Creativity — new options appear",
  "Connectedness — nothing inside you is an enemy",
];

export const IFS_PARTS = [
  {
    id: "managers",
    emoji: "📋",
    name: "Managers — the protectors who plan ahead",
    strapline: "Proactive protection",
    story: `Managers work tirelessly so you never feel the pain again. They organise, perfect, please, criticise and control — all to keep life predictable enough that nothing overwhelming gets through.`,
    signs: [
      "The perfectionist who says nothing is ever good enough",
      "The people-pleaser who reads every room before speaking",
      "The inner critic who gets there before anyone else can",
      "The planner who cannot rest until everything is handled",
      "The one who keeps you small, busy, or endlessly useful",
    ],
    voice: "\"If I stay ahead of it, we'll be safe.\"",
  },
  {
    id: "firefighters",
    emoji: "🚒",
    name: "Firefighters — the protectors who react",
    strapline: "Reactive protection",
    story: `When pain breaks through anyway, firefighters do whatever it takes to put the feeling out — fast. Their methods can look destructive from the outside, but their intention is always to stop unbearable pain.`,
    signs: [
      "Numbing: scrolling, drinking, bingeing, shopping, working",
      "Sudden rage, or the urge to walk out and never come back",
      "Dissociating — going blank, floating away",
      "Self-harm urges, risk-taking, compulsive behaviour",
      "Shutting people out the moment they get close",
    ],
    voice: "\"Make it stop. I don't care what it costs.\"",
  },
  {
    id: "exiles",
    emoji: "🕯️",
    name: "Exiles — the parts carrying the pain",
    strapline: "The protected ones",
    story: `Exiles are usually young. They hold the fear, shame, grief and loneliness that were too much at the time, so they were locked away. Protectors exist entirely to keep them from flooding you — which is why approaching an exile without permission backfires.`,
    signs: [
      "A sudden wave of childlike hurt that feels far too big",
      "Deep shame that says something is wrong with you",
      "Terror of abandonment, or of being seen",
      "Grief that arrives without warning",
      "The feeling of being very small inside an adult life",
    ],
    voice: "\"Please don't leave me. Please see me.\"",
  },
] as const;

export const IFS_NO_BAD_PARTS = `There are no bad parts. The inner critic, the numbing, the rage — every one of them took on its role at a moment when it helped you survive. They're often stuck in the past, still doing a job that ended years ago. When a part feels heard by Self, it doesn't need to shout. Fighting a part only recruits more protection; befriending it is what lets it soften.`;

export const IFS_STEPS = [
  {
    label: "Step 1 — Find and focus",
    note: "Slow down enough to notice one part at a time.",
    steps: [
      "Notice what's loudest right now: a feeling, a thought, a sensation",
      "Find where it lives in your body — chest, throat, stomach, jaw",
      "Turn toward it gently, the way you'd turn toward a nervous animal",
      "Give it a simple name: 'the anxious one', 'the critic', 'the tired one'",
    ],
  },
  {
    label: "Step 2 — Check how you feel toward it",
    note: "This is the whole practice. If you feel anything but openness, you've met another part.",
    steps: [
      "Ask yourself: how do I feel toward this part?",
      "If the answer is irritation, fear or 'I want it gone' — that's a second part",
      "Ask that second part to step back a little, just for now, and see what happens",
      "Keep going until there's some curiosity or warmth available",
    ],
  },
  {
    label: "Step 3 — Befriend and unburden",
    note: "You're building a relationship, not conducting an interview. Slow is faster here.",
    steps: [
      "Ask the part: what do you want me to know?",
      "Ask: what are you afraid would happen if you stopped doing this job?",
      "Thank it — genuinely — for how long it has worked to protect you",
      "Let it know you're here now, and you're not going anywhere",
      "Ask what it needs from you, and whether it's willing to try something new",
    ],
  },
];

export const IFS_SAFETY = `Go slowly, and never override a protector. If a part says no, that no is information, not an obstacle — respect it and the trust builds. Deeper unburdening work, especially with exiles and trauma memories, belongs with a trained IFS therapist alongside you. Everything here is for gentle self-knowledge, not for reopening what needs company to hold.`;

export const IFS_REFLECT = [
  "📋 Which manager runs your day — the perfectionist, the pleaser, the planner?",
  "🚒 What does your system reach for when a feeling gets too big?",
  "🕯️ How old does the hurt feel when it arrives?",
  "💛 What might your harshest part be trying to protect you from?",
  "🌿 Can you thank one part today, even silently?",
];
